// import { GET_CURRENT_USER } from '@/Graphql/queries';
import { ColumnType } from '@/Types/global';
import FitCurveIcon from '@/assets/images/FitCurveIcon.svg';
import FitPrimeIcon from '@/assets/images/FitPrimeIcon.svg';
import FitVarsityIcon from '@/assets/images/FitVarsityIcon.svg';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import Table from '@/components/ui/table';
import { authState } from '@/vars/auth';
import { useQuery, useReactiveVar } from '@apollo/client';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GET_SESSION_BALANCE } from '../sessions/GraphQl/queries';
import { GET_ALL_SUBSCRIPTIONS } from './GraphQL/queries';
import BuySubsctription from './components/BuySubsctription';
import FirstSubscription from './components/FirstSubscription';

const Subscription = () => {
  const [searchParams] = useSearchParams();
  const planIdFromURL = searchParams.get('planId');
  const modalFromURL = searchParams.get('modal');
  const { userDetails } = useReactiveVar(authState);
  const [buyModalOpen, setBuyModalOpen] = useState(modalFromURL ? modalFromURL === 'true' : false);
  const [filterValues, setFilterValues] = useState<{
    [key: string]: string | number | undefined;
  }>({
    page: 0,
  });
  const [defaultPlan, setDefaultPlan] = useState(planIdFromURL ? planIdFromURL : '');
  const [selectedProgramForPurchase, setSelectedProgramForPurchase] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // const { data: currentUser, refetch } = useQuery(GET_CURRENT_USER);

  const {
    data,
    loading: subscriptionLoading,
    refetch: purchaseRefetch,
  } = useQuery(GET_ALL_SUBSCRIPTIONS, {
    variables: {
      filter: {
        traineeId: userDetails?._id,
      },
      search: '',
      limit: 10,
      offset: Math.max(+(filterValues?.['page'] || 0) - 1, 0) * 10,
      sort: { createdAt: -1 },
    },
    fetchPolicy: 'network-only',
  });

  const { data: sessionBalance, loading: balanceLoading } = useQuery(GET_SESSION_BALANCE as any, {
    fetchPolicy: 'network-only',
  });

  const Columns: ColumnType[] = [
    { title: 'Date', id: 'date', isVisible: true, align: 'left' },
    { title: 'Transaction ID', id: 'transId', isVisible: true },
    { title: 'Package', id: 'package', isVisible: true, align: 'center' },
    { title: 'Mode', id: 'mode', isVisible: true, align: 'center' },
    { title: 'Amount', id: 'amount', isVisible: true, align: 'center' },
  ];

  function formatPaymentMethod(method: string): string {
    switch (method) {
      case 'STRIP':
        return 'Stripe';
      case 'STRIPE':
        return 'Stripe';
      case 'OTHER':
        return 'Other';
      case 'INAPP':
        return 'In-App';
      case 'COMPLIMENTARY':
        return 'Complimentary';
      case 'WELCOME_REWARD':
        return 'Welcome Reward';
      default:
        return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase(); // Fallback
    }
  }

  const tableData = useMemo(
    () =>
      data?.getAllPurchase?.map((purchase) => {
        // Format package name based on session count - simplified since we don't have subscriptionType in Purchase
        const formatPackageName = (name: string, sessionCount: number) => {
          if (name === 'WelcomeReward') {
            return `${'Welcome Reward'} - ${sessionCount} Session${sessionCount > 1 ? 's' : ''}`;

          } else {
            return `${name} - ${sessionCount} Session${sessionCount > 1 ? 's' : ''}`;
          }

        };

        return {
          id: purchase?._id,
          date: dayjs(purchase?.paymentDate as string).format('MM/DD/YYYY'),
          transId: purchase?.transactionId,
          package: formatPackageName(purchase?.programDetails?.programName || '', purchase?.sessionCount || 0),
          mode:
            purchase?.paymentMode === 'OTHER'
              ? formatPaymentMethod(purchase.paymentMethod as string)
              : purchase?.paymentMode,
          amount:
            purchase?.localizedPrice && Number(purchase.localizedPrice) !== 0
              ? purchase.localizedPrice
              : '$0',
        };
      }),
    [data],
  );

  const handleClose = () => {
    setBuyModalOpen(false);
    setSelectedProgramForPurchase('');
    setLoading(true);
    setTimeout(() => {
      // refetch();
      purchaseRefetch();
      setLoading(false);
    }, 2000);
  };

  if (loading || subscriptionLoading || balanceLoading) {
    return <LoadingComponent />;
  }

  // Program-specific data with actual balance values from API
  const programs = [
    {
      id: 'fitprime',
      name: 'FitPrime',
      description:
        '1-on-1 personal training. Choose your path — Yoga, Strength, Cardio & more. Pay as you go.',
      icon: FitPrimeIcon,
      sessionsLeft: sessionBalance?.getCurrentUser?.fitPrimeBalance || 0,
    },

    {
      id: 'fitcurve',
      name: 'FitCurve',
      description: 'Lower body sculpting program built for results. Stronger curves in just weeks.',
      icon: FitCurveIcon,
      sessionsLeft: sessionBalance?.getCurrentUser?.fitCurveBalance || 0,
    },
    {
      id: 'fitvarsity',
      name: 'FitVarsity',
      description:
        'Athletic training for ages 13–18. Sport-specific workouts to level up your game.',
      icon: FitVarsityIcon,
      sessionsLeft: sessionBalance?.getCurrentUser?.fitVarsityBalance || 0,
    },

  ];

  return (
    <div className='h-full w-full'>
      {data?.getAllPurchaseCount && data.getAllPurchaseCount > 0 ? (
        <div className='px-4 py-6 sm:px-6 sm:py-8 md:px-10 lg:px-20 lg:py-12'>
          {/* Header Section */}
          <div className='mb-6 md:mb-8'>
            <h1 className='mb-2 text-lg font-semibold text-white md:text-xl'>
              Your Fitness Progress & More to Discover
            </h1>
            <p className='text-sm font-normal text-[#A8ADB9] md:max-w-3xl'>
              See how many sessions you have left in each program—and explore new paths to level up
              your training.
            </p>
          </div>

          {/* Program Cards */}
          <div className='mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8'>
            {programs.map((program) => (
              <div
                key={program.id}
                className='rounded-xl border border-[#515764] px-4 py-6 sm:px-5 sm:py-7'
                style={{
                  background:
                    'linear-gradient(227deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.24) 36%, rgba(255, 255, 255, 0.17) 49%, rgba(255, 255, 255, 0.23) 63%, rgba(255, 255, 255, 0.1) 100%)',
                }}
              >
                <div className='flex h-full flex-col items-center justify-center gap-6 sm:gap-[30px]'>
                  {/* Program Header and Description */}
                  <div className='flex flex-col items-center gap-3'>
                    {/* Program Header */}
                    <div className='flex items-center gap-3'>
                      <img src={program.icon} alt={program.name} className='h-9 w-9 sm:h-10 sm:w-10' />
                      <span className='text-lg font-medium leading-[1.219] tracking-[0.02em] text-white sm:text-xl'>{program.name}</span>
                    </div>

                    {/* Program Description */}
                    <p className='text-center text-sm leading-[1.4] text-[#E2E4E8]'>{program.description}</p>
                  </div>

                  {/* Sessions Left or Start Journey */}
                  <div className='flex flex-col items-center gap-4'>
                    {program.sessionsLeft > 0 ? (
                      <>
                        <span className='text-center text-5xl font-medium leading-[0.75] tracking-[0.06em] text-white sm:text-[56px]'>
                          {program.sessionsLeft.toString().padStart(2, '0')}
                        </span>
                        <span className='text-center text-base font-medium leading-[1.219] tracking-[0.02em] text-white'>Sessions left</span>
                        <Button
                          className='h-9 w-full rounded-xl px-5 py-2 text-xs font-bold leading-[1.4] tracking-[0.02em] text-black'
                          style={{
                            background:
                              'linear-gradient(98deg, rgba(248, 177, 7, 1) 0%, rgba(239, 232, 115, 1) 100%)',
                          }}
                          id={`buy-more-${program.id}`}
                          onClick={() => {
                            setDefaultPlan(program.id);
                            setSelectedProgramForPurchase(program.id);
                            setBuyModalOpen(true);
                          }}
                        >
                          Buy More Sessions
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          className='h-9 w-full rounded-xl px-5 py-2 text-xs font-bold leading-[1.4] tracking-[0.02em] text-black'
                          style={{
                            background:
                              'linear-gradient(98deg, rgba(248, 177, 7, 1) 0%, rgba(239, 232, 115, 1) 100%)',
                          }}
                          id={`start-journey-${program.id}`}
                          onClick={() => {
                            setDefaultPlan(program.id);
                            setSelectedProgramForPurchase(program.id);
                            setBuyModalOpen(true);
                          }}
                        >
                          Start your journey today
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Subscription History */}
          <div className='flex flex-col gap-6 md:gap-8'>
            <div className='flex flex-col gap-2'>
              <span className='text-lg font-semibold text-white md:text-xl'>Subscription History</span>
              <span className='text-sm font-normal text-[#A8ADB9] md:max-w-3xl'>
                View details of your past purchases, including session packages and subscription
                plans. Keep track of your usage and renew as needed.
              </span>
            </div>
            <Table
              id={'purchaseTable'}
              noPadding
              loading={loading}
              columns={Columns}
              setFilterValues={setFilterValues}
              filterValues={filterValues}
              data={tableData?.map((row) => {
                return {
                  cells: {
                    date: row.date,
                    transId: row.transId,
                    package: row.package,
                    mode: row.mode,
                    amount: row.amount,
                  },
                };
              })}
              itemsPerPage={10}
              dataCount={data?.getAllPurchaseCount}
              onPageChange={(page) => setFilterValues({ page: page })}
              page={toNumber(filterValues?.page) || 0}
              paginationClassName='w-auto'
            />
          </div>
        </div>
      ) : (
        <div className='flex items-center justify-center'>
          <FirstSubscription setOpen={setBuyModalOpen} setDefaultPlan={setDefaultPlan} />
        </div>
      )}
      {buyModalOpen && (
        <BuySubsctription
          open={buyModalOpen}
          handleClose={handleClose}
          defaultPlan={defaultPlan}
          selectedProgram={selectedProgramForPurchase}
        />
      )}
    </div>
  );
};

export function LoadingComponent() {
  return (
    <div className='h-full w-full'>
      <div className='px-4 py-6 sm:px-6 sm:py-8 md:px-10 lg:px-20 lg:py-12'>
        {/* Header Section Loading */}
        <div className='mb-6 md:mb-8'>
          <Skeleton className='h-7 w-80 mb-2 bg-bgSecondary' />
          <Skeleton className='h-4 w-full max-w-96 bg-bgSecondary' />
        </div>

        {/* Program Cards Loading */}
        <div className='mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-8'>
          {[...Array(3)].map((_, index) => (
            <div key={index}>
              <LoadingProgramCard />
            </div>
          ))}
        </div>

        {/* Subscription History Loading */}
        <div className='flex flex-col gap-6 md:gap-8'>
          <div className='flex flex-col gap-2'>
            <Skeleton className='h-7 w-48 mb-2 bg-bgSecondary' />
            <Skeleton className='h-4 w-full max-w-96 bg-bgSecondary' />
          </div>
          <LoadingTable />
        </div>
      </div>
    </div>
  );
}

function LoadingProgramCard() {
  return (
    <div
      className='rounded-xl border border-[#515764] p-6 sm:p-7'
      style={{
        background:
          'linear-gradient(227deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.24) 36%, rgba(255, 255, 255, 0.17) 49%, rgba(255, 255, 255, 0.23) 63%, rgba(255, 255, 255, 0.1) 100%)',
      }}
    >
      <div className='flex flex-col items-center gap-4'>
        <div className='flex items-center gap-3'>
          <Skeleton className='w-6 h-6 bg-bgSecondary' />
          <Skeleton className='h-6 w-24 bg-bgSecondary' />
        </div>
        <Skeleton className='h-4 w-full bg-bgSecondary' />
        <Skeleton className='h-4 w-3/4 bg-bgSecondary' />
        <Skeleton className='h-16 w-20 bg-bgSecondary' />
        <Skeleton className='h-4 w-24 bg-bgSecondary' />
        <Skeleton className='h-9 w-full bg-bgSecondary' />
      </div>
    </div>
  );
}

function LoadingTable() {
  return (
    <div className='flex flex-col gap-4'>
      <Skeleton className='h-10 bg-bgSecondary' />
      {[...Array(5)].map((_, index) => (
        <Skeleton key={index} className='h-12 bg-bgSecondary' />
      ))}
    </div>
  );
}

export default Subscription;
